import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import classNames from "classnames"

import { ID, Idea, Location } from "../../../../../model/types"
import IdeaBox from "../../../../components/idea_box"
import { useNana } from "../../../../hooks/useNana"
import { MosaicPageState } from "../../mosaic"
import MoveCard, {
  CardMeta,
  CardPreviewText,
  CardPrimaryButton,
  previewFromHtml,
  sourceDisplayTitle,
} from "../move_card"
import NeighborChip from "../neighbor_chip"

const textShouldBeWhiteOrBlack = (bgColour: string) => {
  const r = parseInt(bgColour.slice(1, 3), 16)
  const g = parseInt(bgColour.slice(3, 5), 16)
  const b = parseInt(bgColour.slice(5, 7), 16)
  const brightness = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b)
  return brightness < 140
}

const gridDistance = (a: Location, b: Location): number => {
  const dx = a.gx - b.gx
  const dy = a.gy - b.gy
  return Math.sqrt(dx * dx + dy * dy)
}

/** Nearest goal(s); include ties within ~one cell of the minimum distance. */
export const nearestGoalsTo = (
  location: Location,
  goals: Idea[],
  excludeId?: ID,
  maxShow = 4,
): Idea[] => {
  const candidates = goals
    .filter((g) => g.id !== excludeId)
    .map((g) => ({ goal: g, d: gridDistance(g.location, location) }))
    .sort((a, b) => a.d - b.d)

  if (candidates.length === 0) return []

  const minD = candidates[0].d
  // Roughly equal: within the nearest band (+1 grid unit of slack)
  const threshold = minD + 1
  return candidates
    .filter((c) => c.d <= threshold)
    .slice(0, maxShow)
    .map((c) => c.goal)
}

export default function WanderCard({
  ideaId,
  onReroll,
  onObjectGone,
}: {
  ideaId: ID
  onReroll: () => void
  onObjectGone: () => void
}) {
  const { model } = useNana()
  const navigate = useNavigate()
  const idea = model.getIdea(ideaId)

  useEffect(() => {
    if (!idea) onObjectGone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea])

  if (!idea) return null

  const open = () => {
    MosaicPageState.forceOpenLocation = idea.location
    navigate("/app")
  }

  const neighbors = model
    .getNearestIdeasTo(idea.location)
    .filter((n) => n.type !== "goal")
    .slice(0, 5)

  const nearbyGoals = nearestGoalsTo(
    idea.location,
    model.getGoals(),
    idea.id,
  )

  const lightText = idea.colour ? textShouldBeWhiteOrBlack(idea.colour) : false
  const body = previewFromHtml(idea.content, 280)
  const title =
    idea.title || previewFromHtml(idea.content, 80) || idea.memo || "Untitled"

  let sourceLabel: string | null = null
  if (idea.sourceType === "self") {
    sourceLabel = "myself"
  } else if (idea.sourceId) {
    const source = model.getSource(idea.sourceId)
    if (source) sourceLabel = sourceDisplayTitle(source)
  }

  return (
    <MoveCard
      move="wander"
      onReroll={onReroll}
      onPrimary={open}
      actions={<CardPrimaryButton onClick={open}>Open on mosaic</CardPrimaryButton>}
    >
      <div className="flex min-h-[7.5rem] flex-1 flex-col gap-3">
        <div
          className={classNames(
            "flex items-center gap-3 overflow-hidden rounded-md px-3.5 py-3",
            lightText ? "text-white" : "text-stone-900",
          )}
          style={{
            background: idea.colour
              ? `linear-gradient(135deg, ${idea.colour} 0%, ${idea.colour}cc 100%)`
              : "linear-gradient(135deg, #e7e5e4 0%, #d6d3d1 100%)",
          }}
        >
          <IdeaBox idea={idea} shadow={false} />
          <div className="min-w-0">
            <div className="line-clamp-2 text-[1.05rem] font-semibold leading-snug tracking-[-0.015em]">
              {idea.title ? (
                idea.title
              ) : (
                <span className="italic opacity-90">{title}</span>
              )}
            </div>
            {idea.type === "goal" && (
              <div className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.14em] opacity-75">
                Goal
              </div>
            )}
          </div>
        </div>

        {body && idea.title && (
          <CardPreviewText lines={3}>{body}</CardPreviewText>
        )}

        {nearbyGoals.length > 0 && (
          <div className="flex items-center gap-2 pt-0.5">
            <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-stone-400 dark:text-stone-500">
              {nearbyGoals.length === 1 ? "Goal" : "Goals"}
            </span>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {nearbyGoals.map((g) => (
                <NeighborChip key={String(g.id)} idea={g} />
              ))}
            </div>
          </div>
        )}

        {neighbors.length > 0 && (
          <div className="flex items-center gap-2 pt-0.5">
            <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-stone-400 dark:text-stone-500">
              Nearby
            </span>
            <div className="flex items-center gap-1.5">
              {neighbors.map((n) => (
                <NeighborChip key={String(n.id)} idea={n} />
              ))}
            </div>
          </div>
        )}

        <CardMeta>
          {sourceLabel ? (
            <span className="truncate opacity-70">from {sourceLabel}</span>
          ) : (
            <span className="opacity-70">Open on mosaic</span>
          )}
        </CardMeta>
      </div>
    </MoveCard>
  )
}
